import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Toolbar,
  Typography,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import type { ReactNode } from "react";

const APP_NAME = "TMHub Careers";

export function AuthLayout({ children }: { children: ReactNode }) {
  const auth = useAuth();

  if (auth.isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (auth.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" gutterBottom>
          Authentication error
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {auth.error.message}
        </Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => auth.signinRedirect()}>
          Try sign in
        </Button>
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
          px: 2,
        }}
      >
        <Typography variant="h5" component="h2" align="center" fontWeight={600}>
          {APP_NAME}
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary">
          Sign in to continue
        </Typography>
        <Button variant="contained" onClick={() => auth.signinRedirect()}>
          Sign in
        </Button>
      </Box>
    );
  }

  return (
    <>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar variant="dense">
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {APP_NAME}
          </Typography>
          <Button
            color="inherit"
            onClick={async () => {
              await auth.removeUser();
              const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID?.trim()!;
              const logoutUri = window.location.origin;
              const cognitoDomain = `https://tmhub-ops-ccl.auth.${process.env.REACT_APP_COGNITO_REGION?.trim()!}.amazoncognito.com`;
              window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
            }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>
      {children}
    </>
  );
}
